import {
  Box,
  Card,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Popover,
  TextField,
  Theme,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import { Children, useEffect, useState } from "react";
import { Link } from "react-admin";
import { useHistory } from "react-router-dom";
import classnames from "classnames";
import { VehicleBreadcrumbSearchField } from "../../assets/maintenance-dashboard/VehicleBreadcrumbSearchField";
import { KeyboardBackspace, Search } from "@material-ui/icons";
import styles from "./StateBreadcrumb.module.css";
import { useAppData } from "../../../common/hooks/useAppData";

const useStyles = makeStyles((theme: Theme) => ({
  breadcrumbViewContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "5px",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dropdownTypography: {
    padding: theme.spacing(2),
  },
  searchField: {
    width: `calc(100% - ${theme.spacing(2)}px)`,
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginBottom: "8px",
  },
  anchorDiv: {
    minHeight: "48px",
    minWidth: "48px",
  },
  popupContainer: {
    overflow: "hidden",
  },
  dropdownResults: {
    overflowY: "auto",
    maxHeight: "75vh",
  },
  marginBottom4: {
    marginBottom: "4px",
  },
  marginDense: {
    marginTop: 0,
    marginBottom: 0,
  },
}));

export interface SelectedState {
  stateCode?: string | null;
  name?: string | null;
  id?: number | null;
}

interface Props {
  state?: SelectedState;
  getBasePath: (state?: SelectedState) => string;
  getLink?: (state?: SelectedState) => string;
  getBackLink?: (state?: SelectedState) => string;
}

interface StateNameDisplayProps {
  stateCode?: string | null;
  name?: string | null;
}

interface StateNameBreadcrumbViewProps {
  children: React.ReactNode;
  getBasePath: (state?: SelectedState) => string;
  selectedState?: SelectedState;
  getBackLink?: (state?: SelectedState) => string;
}

const StateNameDisplay = ({ stateCode, name }: StateNameDisplayProps) => (
  <Box display="flex" flexDirection="row" gridGap={5}>
    <sup>
      <Typography>{stateCode}</Typography>
    </sup>
    <Typography variant="h6">{name}</Typography>
  </Box>
);

export const StateNameBreadcrumbView = ({
  children,
  getBasePath,
  selectedState,
  getBackLink,
}: StateNameBreadcrumbViewProps) => {
  const [statesList, setStatesList] = useState<SelectedState[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const data = useAppData((x) => x.countries)?.find((x) => x.name === "USA")
    ?.states;

  useEffect(() => {
    if (!data) {
      return;
    }

    const sortedData = data.sort((a, b) => (a.id ?? 0) - (b.id ?? 0));
    setStatesList(sortedData);
  }, [data]);

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStateSelected = (state?: SelectedState) => {
    handleClose();
    if (getBasePath && selectedState?.id !== state?.id) {
      history.push(getBasePath(state));
    }
  };

  const handleStateSearch = (searchText: string) => {
    if (!data) {
      return;
    }

    const filteredData = data.filter((x) => {
      return (
        x.stateCode?.toLowerCase().includes(searchText.toLowerCase()) ||
        x.name?.toLowerCase().includes(searchText.toLowerCase())
      );
    });

    setStatesList(filteredData);
  };

  const classes = useStyles();
  const dropdownOpen = Boolean(anchorEl);
  return (
    <div className={classes.breadcrumbViewContainer}>
      <>
        {Children.only(children)}
        <div className={styles.wrapper}>
          {getBackLink ? (
            <IconButton
              onClick={() => history.push(getBackLink(selectedState))}
            >
              <KeyboardBackspace />
            </IconButton>
          ) : null}
          <TextField
            disabled={!!anchorEl}
            variant="outlined"
            margin="dense"
            className={`appbar-filter appbar-simple-search-field ${
              anchorEl ? "hidden" : ""
            } ${classes.marginDense}`}
            label=""
            value=""
            onClick={handleClick}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Popover
            className={classes.popupContainer}
            open={dropdownOpen}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
          >
            <Box>
              <Card>
                <VehicleBreadcrumbSearchField
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    handleStateSearch(e.target.value);
                  }}
                  className={classes.searchField}
                />
                <Divider className={classes.marginBottom4} />
              </Card>
              <Card
                className={classnames(
                  classes.dropdownResults,
                  "custom-scrollbar"
                )}
              >
                {statesList.length ? (
                  <List dense>
                    {statesList.map((x) => (
                      <ListItem
                        key={x.id}
                        button
                        onClick={() => handleStateSelected(x)}
                      >
                        <ListItemText
                          primary={
                            <Box display="flex" flexDirection="row" gridGap={5}>
                              <sup>{x.stateCode}</sup>
                              <Typography variant="body1">{x.name}</Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography className={classes.dropdownTypography}>
                    No states found.
                  </Typography>
                )}
              </Card>
            </Box>
          </Popover>
        </div>
      </>
    </div>
  );
};

export const StateBreadcrumb = ({
  state,
  getLink,
  getBasePath,
  getBackLink,
}: Props) => {
  const [selectedState, setSelectedState] = useState<SelectedState>();
  const data = useAppData((x) => x.countries)?.find((x) => x.name === "USA")
    ?.states;

  useEffect(() => {
    if (state) {
      setSelectedState(data?.find((x) => x.id === state.id));
    }
  }, [state, data]);

  const link = getLink ? getLink(selectedState) : undefined;
  const stateCode = selectedState?.stateCode ?? "";
  const name = selectedState?.name ?? "";

  if (link) {
    return (
      <>
        <StateNameBreadcrumbView
          getBasePath={getBasePath}
          selectedState={selectedState}
          getBackLink={getBackLink}
        >
          <Link to={link}>
            <StateNameDisplay stateCode={stateCode} name={name} />
          </Link>
        </StateNameBreadcrumbView>
      </>
    );
  }

  return (
    <StateNameBreadcrumbView
      getBasePath={getBasePath}
      selectedState={selectedState}
    >
      <StateNameDisplay stateCode={stateCode} name={name} />
    </StateNameBreadcrumbView>
  );
};
